import React, { useEffect, useRef, useState } from 'react';
import { T, NUMERIC } from './orders.theme';
import { COPY, money } from './orders.copy';
import { AlertIcon } from './OrdersIcons';
import { PulsingDots } from './OrderStatusPill';

/**
 * Cancelling asks, because on the server it voids the invoice transaction and
 * reverses the customer's balance. The reason it collects is stored against
 * the order, so the button stays dead until one is typed.
 *
 * While the request is in flight the field locks and the dialog cannot be
 * dismissed — a half-cancelled order is not a state anyone should be able to
 * walk away from.
 */
export default function CancelOrderDialog({
  entry,
  currency,
  isCanceling,
  error,
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState('');
  const fieldRef = useRef(null);

  useEffect(() => {
    setReason('');
    const id = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [entry?.id]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isCanceling) {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, isCanceling]);

  if (!entry) return null;

  const trimmed = reason.trim();
  const armed = trimmed.length > 0 && !isCanceling;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={COPY.cancelTitle(entry.orderNumber)}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isCanceling) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(21,24,29,0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 452,
          maxWidth: '100%',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          boxShadow: T.dialog,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <div
            style={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: T.radiusPill,
              background: T.dangerTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertIcon size={18} color={T.danger} width={1.9} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
              {COPY.cancelTitle(entry.orderNumber)}
            </span>
            <span style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.55 }}>
              {entry.isInvoice && entry.customerName ? (
                <>
                  This voids the invoice and takes{' '}
                  <strong style={{ color: T.ink, ...NUMERIC }}>
                    {money(entry.total, currency)}
                  </strong>{' '}
                  back off <strong style={{ color: T.ink }}>{entry.customerName}</strong>'s
                  balance. It cannot be undone.
                </>
              ) : (
                <>
                  This removes{' '}
                  <strong style={{ color: T.ink, ...NUMERIC }}>
                    {money(entry.total, currency)}
                  </strong>{' '}
                  from today's takings. It cannot be undone.
                </>
              )}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label
            htmlFor="cancel-reason"
            style={{ fontSize: 12, fontWeight: 600, color: T.ink }}
          >
            {COPY.cancelReasonLabel} <span style={{ color: T.danger }}>*</span>
          </label>
          <textarea
            id="cancel-reason"
            ref={fieldRef}
            value={reason}
            readOnly={isCanceling}
            onChange={(e) => setReason(e.target.value)}
            placeholder={COPY.cancelReasonPlaceholder}
            rows={3}
            style={{
              resize: 'none',
              borderRadius: T.radiusControl,
              background: T.surface,
              border: `${trimmed ? 1.5 : 1}px solid ${trimmed ? T.brand : T.border}`,
              padding: '9px 11px',
              fontSize: 12.5,
              lineHeight: 1.5,
              color: T.ink,
              font: 'inherit',
              outline: 'none',
            }}
          />
        </div>

        {error && (
          <span style={{ fontSize: 12.5, color: T.danger, lineHeight: 1.5 }}>{error}</span>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isCanceling}
            style={{
              height: 38,
              padding: '0 18px',
              borderRadius: T.radiusControl,
              background: T.surface,
              border: `1px solid ${T.borderStrong}`,
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              cursor: isCanceling ? 'not-allowed' : 'pointer',
              opacity: isCanceling ? 0.6 : 1,
              font: 'inherit',
            }}
          >
            {COPY.keepOrder}
          </button>
          <button
            type="button"
            onClick={() => armed && onConfirm(entry.id, trimmed)}
            disabled={!armed}
            style={{
              height: 38,
              minWidth: 132,
              padding: '0 18px',
              borderRadius: T.radiusControl,
              background: armed ? T.danger : T.dangerMuted,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: T.surface,
              cursor: armed ? 'pointer' : 'not-allowed',
              font: 'inherit',
            }}
          >
            {isCanceling ? <PulsingDots /> : COPY.cancelOrder}
          </button>
        </div>
      </div>
    </div>
  );
}
